// ---------------------------------------------------------------------------
// ViaCEP — Service Layer (autofill de endereço)
//
// ViaCEP é um serviço PÚBLICO externo (sem auth, CORS liberado). NÃO passa pelo
// axios singleton do app: aquele carrega baseURL/withCredentials/interceptor de
// auth que só fazem sentido contra o NOSSO backend. Aqui usamos `fetch` direto.
//
// A tradução ViaCEP (pt-BR) → contrato de endereço do MWA acontece na fronteira
// deste service — o componente recebe já os campos com o nome do contrato.
// ---------------------------------------------------------------------------

// CEP não encontrado na base do ViaCEP (payload `{ erro: true }`). Erro tipado
// para o chamador distinguir "não achou" de "falha de rede" e dar o feedback
// certo (erro nunca silencioso).
export class CepNotFoundError extends Error {
   constructor(message = 'CEP não encontrado.') {
      super(message);
      this.name = 'CepNotFoundError';
   }
}

function onlyDigits(value) {
   return String(value ?? '').replace(/\D/g, '');
}

// Consulta um CEP e devolve os campos já mapeados para o contrato de endereço.
// Retorna `null` quando o CEP não tem 8 dígitos (guard — nem chega a bater na
// rede). Lança `CepNotFoundError` para CEP inexistente e `Error` genérico para
// falha de rede/HTTP.
export async function lookupCep(rawCep) {
   const cep = onlyDigits(rawCep).substring(0, 8);
   if (cep.length !== 8) return null;

   const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
   if (!response.ok) {
      throw new Error('Falha ao consultar o CEP.');
   }

   const data = await response.json();
   // ViaCEP sinaliza CEP inexistente com `erro: true` (string ou boolean).
   if (data?.erro) {
      throw new CepNotFoundError();
   }

   return {
      postal_code: data.cep ?? '',
      street: data.logradouro ?? '',
      neighborhood: data.bairro ?? '',
      city: data.localidade ?? '',
      state: data.uf ?? '',
      // Preenchimento fraco: ViaCEP manda coisas tipo "lado ímpar"; o chamador
      // decide não sobrescrever se o operador já digitou algo.
      complement: data.complemento ?? '',
   };
}
